import { useMemo, useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Search, AlertTriangle, Info } from 'lucide-react';
import { MetaApp, MetaPage, PageGroup, CreatePageGroupInput } from '../types/meta.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

interface CreateEditPageGroupDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    apps: MetaApp[];                 // todos os apps que o user pode usar
    pages: MetaPage[];                // todas as páginas do user
    existingGroups?: PageGroup[];     // grupos já criados — usado pra esconder apps já usados
    editingGroup?: PageGroup | null;  // null/undefined = criar novo
    onSubmit: (data: CreatePageGroupInput) => void;
    isLoading: boolean;
}

export default function CreateEditPageGroupDialog({
    open,
    onOpenChange,
    apps,
    pages,
    existingGroups = [],
    editingGroup,
    onSubmit,
    isLoading,
}: CreateEditPageGroupDialogProps) {
    const [name, setName] = useState('');
    const [appId, setAppId] = useState<number | null>(null);
    const [selectedPageIds, setSelectedPageIds] = useState<Set<number>>(new Set());
    const [pageSearch, setPageSearch] = useState('');
    /** Filtro por categoria — 'all' = sem filtro; 'none' = só sem categoria;
     *  caso contrário é o ID da categoria (chatmeta_page_categories.id) como string. */
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    /** Moves a confirmar — { pageId, fromGroupName }[]. Vazio = nenhum move pendente. */
    const [pendingMoves, setPendingMoves] = useState<{ pageId: number; pageName: string; fromGroupName: string }[]>([]);

    // Reset/popula form quando abre
    useEffect(() => {
        if (open) {
            if (editingGroup) {
                setName(editingGroup.name);
                setAppId(editingGroup.app_id);
                setSelectedPageIds(new Set(editingGroup.pages.map(p => p.id)));
            } else {
                setName('');
                setAppId(null);
                setSelectedPageIds(new Set());
            }
            setPageSearch('');
            setCategoryFilter('all');
        }
    }, [open, editingGroup]);

    // Apps que já estão em uso por outros grupos. Cada app só pode ter UM grupo
    // ativo — se já tem, esconde do dropdown na criação. Na edição, exclui o
    // grupo atual do "já usado" pra ainda permitir manter o mesmo app.
    const usedAppIds = useMemo(() => {
        const ids = new Set<number>();
        existingGroups.forEach(g => {
            if (!editingGroup || g.id !== editingGroup.id) ids.add(g.app_id);
        });
        return ids;
    }, [existingGroups, editingGroup]);

    // Apps disponíveis pro dropdown — ativos e ainda não usados por outros grupos.
    const availableApps = useMemo(() => {
        return apps.filter(a => a.is_active && !usedAppIds.has(a.id));
    }, [apps, usedAppIds]);

    // Pra cada página, saber se ela tem token no app selecionado.
    // Backend manda essa info em MetaPage.apps[].
    const pageHasTokenInApp = (page: MetaPage, targetAppId: number | null): boolean => {
        if (targetAppId == null) return false;
        return !!page.apps?.some(a => a.app_id_pk === targetAppId && a.is_active);
    };

    // Mapa: page_id (chatmeta_pages.id) → { groupId, groupName } pra páginas que
    // já estão em OUTRO grupo (não o que estamos editando). Usado pra mostrar
    // badge "já em: X" e confirmar move no save.
    const pageOtherGroup = useMemo(() => {
        const map = new Map<number, { groupId: number; groupName: string }>();
        existingGroups.forEach(g => {
            if (editingGroup && g.id === editingGroup.id) return;
            g.pages.forEach(p => {
                map.set(p.id, { groupId: g.id, groupName: g.name });
            });
        });
        return map;
    }, [existingGroups, editingGroup]);

    // Páginas elegíveis = só as que têm token no app escolhido. Exceção: se
    // estamos editando um grupo e a página já era membro, mostra mesmo sem
    // token pra user poder removê-la ou ver o estado órfão.
    const eligiblePages = useMemo(() => {
        if (appId == null) return [] as MetaPage[];
        return pages.filter(p =>
            pageHasTokenInApp(p, appId) || selectedPageIds.has(p.id)
        );
    }, [pages, appId, selectedPageIds]);

    // Opções de categoria derivadas das páginas elegíveis (só faz sentido filtrar
    // por categorias que aparecem nas páginas que estão na lista).
    const categoryOptions = useMemo(() => {
        const map = new Map<number, string>();
        eligiblePages.forEach(p => {
            if (p.internal_category_id && p.internal_category_name) {
                map.set(p.internal_category_id, p.internal_category_name);
            }
        });
        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [eligiblePages]);

    // Quantas páginas elegíveis estão sem categoria (pro item "Sem categoria")
    const eligibleWithoutCategoryCount = useMemo(
        () => eligiblePages.filter(p => !p.internal_category_id).length,
        [eligiblePages],
    );

    // Páginas filtradas: search + categoria
    const filteredPages = useMemo(() => {
        const q = pageSearch.toLowerCase();
        return eligiblePages.filter(p => {
            if (pageSearch && !p.page_name.toLowerCase().includes(q)) return false;
            if (categoryFilter === 'all') return true;
            if (categoryFilter === 'none') return !p.internal_category_id;
            return String(p.internal_category_id) === categoryFilter;
        });
    }, [eligiblePages, pageSearch, categoryFilter]);

    // Quantidade de páginas que ficaram de fora por não ter token (info ao user)
    const hiddenWithoutToken = useMemo(() => {
        if (appId == null) return 0;
        return pages.filter(p =>
            !pageHasTokenInApp(p, appId) && !selectedPageIds.has(p.id)
        ).length;
    }, [pages, appId, selectedPageIds]);

    const orphansSelected = useMemo(() => {
        if (appId == null) return 0;
        return Array.from(selectedPageIds).filter(pid => {
            const page = pages.find(p => p.id === pid);
            return page && !pageHasTokenInApp(page, appId);
        }).length;
    }, [selectedPageIds, appId, pages]);

    const togglePage = (pageId: number) => {
        const next = new Set(selectedPageIds);
        if (next.has(pageId)) next.delete(pageId);
        else next.add(pageId);
        setSelectedPageIds(next);
    };

    const selectAllFiltered = () => {
        const next = new Set(selectedPageIds);
        filteredPages.forEach(p => next.add(p.id));
        setSelectedPageIds(next);
    };

    const deselectAllFiltered = () => {
        const next = new Set(selectedPageIds);
        filteredPages.forEach(p => next.delete(p.id));
        setSelectedPageIds(next);
    };

    const doSubmit = () => {
        if (!name.trim() || appId == null) return;
        onSubmit({
            name: name.trim(),
            app_id: appId,
            page_ids: Array.from(selectedPageIds),
        });
        setPendingMoves([]);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || appId == null) return;

        // Páginas selecionadas que estão em OUTRO grupo → vão ser movidas
        const moves: { pageId: number; pageName: string; fromGroupName: string }[] = [];
        selectedPageIds.forEach(pid => {
            const other = pageOtherGroup.get(pid);
            if (other) {
                const page = pages.find(p => p.id === pid);
                moves.push({
                    pageId: pid,
                    pageName: page?.page_name || `página #${pid}`,
                    fromGroupName: other.groupName,
                });
            }
        });

        if (moves.length > 0) {
            setPendingMoves(moves);
            return;  // espera confirmação no AlertDialog
        }

        doSubmit();
    };

    const canSubmit = name.trim().length > 0 && appId != null && !isLoading;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        {editingGroup ? 'Editar Grupo' : 'Novo Grupo de Páginas'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Define qual app as páginas do grupo vão usar nos envios.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto pr-1">
                    {/* Nome */}
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700 font-medium">
                            Nome do Grupo <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Frota Dev 1"
                            required
                        />
                    </div>

                    {/* App */}
                    <div className="space-y-2">
                        <Label htmlFor="app" className="text-gray-700 font-medium">
                            App vinculado <span className="text-red-500">*</span>
                        </Label>
                        <Select
                            value={appId ? String(appId) : ''}
                            onValueChange={(v) => setAppId(v ? parseInt(v, 10) : null)}
                        >
                            <SelectTrigger id="app">
                                <SelectValue placeholder="Selecione o app..." />
                            </SelectTrigger>
                            <SelectContent>
                                {availableApps.length === 0 && (
                                    <div className="px-2 py-3 text-sm text-gray-500">
                                        Todos os apps já estão vinculados a grupos.
                                    </div>
                                )}
                                {availableApps.map((app) => (
                                    <SelectItem key={app.id} value={String(app.id)}>
                                        {app.app_name}
                                        {app.project_id != null && (
                                            <span className="text-xs text-violet-600 ml-2">
                                                ({app.project_name || `projeto #${app.project_id}`})
                                            </span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                            As páginas do grupo enviarão usando o token desse app.
                        </p>
                    </div>

                    {/* Aviso sobre páginas órfãs */}
                    {orphansSelected > 0 && (
                        <Alert className="border-amber-200 bg-amber-50">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-sm text-amber-800">
                                <strong>{orphansSelected}</strong> página{orphansSelected !== 1 ? 's' : ''} selecionada{orphansSelected !== 1 ? 's' : ''} sem token nesse app.
                                Será{orphansSelected !== 1 ? 'ão' : ''} ignorada{orphansSelected !== 1 ? 's' : ''} no envio até reconectar a página com o app escolhido.
                            </AlertDescription>
                        </Alert>
                    )}

                    {appId == null && (
                        <Alert className="border-blue-200 bg-blue-50">
                            <Info className="h-4 w-4 text-blue-600" />
                            <AlertDescription className="text-sm text-blue-800">
                                Selecione um app primeiro pra ver quais páginas têm token nele.
                            </AlertDescription>
                        </Alert>
                    )}

                    {appId != null && hiddenWithoutToken > 0 && (
                        <Alert className="border-gray-200 bg-gray-50">
                            <Info className="h-4 w-4 text-gray-500" />
                            <AlertDescription className="text-xs text-gray-600">
                                <strong>{hiddenWithoutToken}</strong> página{hiddenWithoutToken !== 1 ? 's' : ''} oculta{hiddenWithoutToken !== 1 ? 's' : ''} por não ter{hiddenWithoutToken !== 1 ? 'em' : ''} token nesse app.
                                {' '}Conecte a conta no app pra elas aparecerem aqui.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Páginas */}
                    <div className="space-y-2">
                        {(() => {
                            const isFiltered = pageSearch !== '' || categoryFilter !== 'all';
                            const visibleCount = filteredPages.length;
                            const visibleSelectedCount = filteredPages.reduce(
                                (acc, p) => acc + (selectedPageIds.has(p.id) ? 1 : 0), 0
                            );
                            const hasSelectionsOutsideFilter = isFiltered
                                && selectedPageIds.size > visibleSelectedCount;
                            const selectAllLabel = isFiltered
                                ? `Selecionar visíveis (${visibleCount})`
                                : 'Selecionar todas';
                            const clearLabel = isFiltered
                                ? `Desmarcar visíveis (${visibleSelectedCount})`
                                : 'Limpar';

                            return (
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <Label className="text-gray-700 font-medium">
                                        Páginas do Grupo <span className="text-gray-500 font-normal">({selectedPageIds.size} selecionada{selectedPageIds.size !== 1 ? 's' : ''})</span>
                                    </Label>
                                    <div className="flex gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={selectAllFiltered}
                                            disabled={visibleCount === 0 || visibleSelectedCount === visibleCount}
                                            className="text-blue-600 hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
                                        >
                                            {selectAllLabel}
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <button
                                            type="button"
                                            onClick={deselectAllFiltered}
                                            disabled={visibleSelectedCount === 0}
                                            className="text-gray-500 hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
                                        >
                                            {clearLabel}
                                        </button>
                                        {hasSelectionsOutsideFilter && (
                                            <>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedPageIds(new Set())}
                                                    className="text-red-500 hover:underline"
                                                    title="Remove todas as seleções, inclusive as fora do filtro atual"
                                                >
                                                    Limpar tudo
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    placeholder="Buscar página..."
                                    className="pl-9"
                                    value={pageSearch}
                                    onChange={(e) => setPageSearch(e.target.value)}
                                />
                            </div>

                            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Todas categorias" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas categorias</SelectItem>
                                    {eligibleWithoutCategoryCount > 0 && (
                                        <SelectItem value="none">
                                            Sem categoria ({eligibleWithoutCategoryCount})
                                        </SelectItem>
                                    )}
                                    {categoryOptions.map(c => (
                                        <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                            {filteredPages.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-8">
                                    Nenhuma página encontrada.
                                </p>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {filteredPages.map((page) => {
                                        const hasToken = pageHasTokenInApp(page, appId);
                                        const isSelected = selectedPageIds.has(page.id);
                                        const otherGroup = pageOtherGroup.get(page.id);
                                        return (
                                            <label
                                                key={page.id}
                                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                                                    isSelected ? 'bg-blue-50/40' : ''
                                                }`}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => togglePage(page.id)}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {page.page_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 font-mono">{page.page_id}</p>
                                                </div>
                                                {otherGroup && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-[10px] py-0 h-5 border-orange-200 bg-orange-50/50 text-orange-700 whitespace-nowrap"
                                                        title={`Marcar vai mover a página pro grupo atual e remover de ${otherGroup.groupName}`}
                                                    >
                                                        já em: {otherGroup.groupName}
                                                    </Badge>
                                                )}
                                                {appId != null && (
                                                    hasToken ? (
                                                        <Badge variant="outline" className="text-[10px] py-0 h-5 border-emerald-200 bg-emerald-50/50 text-emerald-700">
                                                            ✓ token OK
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-[10px] py-0 h-5 border-amber-200 bg-amber-50/50 text-amber-700">
                                                            sem token
                                                        </Badge>
                                                    )
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </form>

                <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-gray-100">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        {isLoading ? 'Salvando...' : editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Confirmação de move quando o user marca páginas que já estão em outro grupo */}
            <ConfirmDialog
                open={pendingMoves.length > 0}
                onOpenChange={(o) => { if (!o) setPendingMoves([]); }}
                variant="warning"
                title={`Mover ${pendingMoves.length} página${pendingMoves.length !== 1 ? 's' : ''} pra esse grupo?`}
                description={
                    <div className="space-y-2 text-sm">
                        <p>
                            {pendingMoves.length === 1 ? 'Essa página já está em outro grupo' : 'Essas páginas já estão em outros grupos'}.
                            Cada página só pode estar em <strong>1 grupo</strong> — salvar vai movê-{pendingMoves.length === 1 ? 'la' : 'las'}.
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 max-h-48 overflow-y-auto bg-gray-50 rounded p-3">
                            {pendingMoves.map(m => (
                                <li key={m.pageId}>
                                    <strong>{m.pageName}</strong> — de <span className="text-orange-700">{m.fromGroupName}</span> → <span className="text-emerald-700">{name}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                }
                confirmLabel="Mover e salvar"
                cancelLabel="Cancelar"
                onConfirm={doSubmit}
                isLoading={isLoading}
            />
        </Dialog>
    );
}
